import React, { useState, useEffect } from 'react';

/**
 * SummaryPanel — fetches the pipeline-generated HTML summary from Google Drive,
 * extracts the structured content (speaker, title, event, takeaways),
 * and renders it with Jony Ive-inspired progressive disclosure styling.
 *
 * Pipeline content is preserved exactly — only the presentation changes.
 */
const SummaryPanel = ({ summaryDocId, themeColor, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const accent = themeColor || '#1A5632';

  useEffect(() => {
    if (!summaryDocId) {
      setError('No summary available for this lecture.');
      setLoading(false);
      return;
    }

    const fetchAndParse = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = `https://dlmcxxzohervnbixauhs.supabase.co/functions/v1/get-summary-html?docx_id=${summaryDocId}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch summary (${response.status})`);

        const rawHtml = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(rawHtml, 'text/html');
        const container = doc.querySelector('.container') || doc.body;

        // Extract structured data from pipeline HTML
        const h1 = container.querySelector('h1');
        const speaker = h1?.textContent?.replace(/^Speaker:\s*/i, '').trim() || '';

        // The italic h2 is the lecture title
        const h2s = Array.from(container.querySelectorAll('h2'));
        const titleH2 = h2s.find(h => h.style?.fontStyle === 'italic' || h.getAttribute('style')?.includes('italic'));
        const title = titleH2?.textContent?.trim() || '';

        // Event info h2
        const eventH2 = h2s.find(h => h.textContent?.startsWith('Event:'));
        const eventName = eventH2?.textContent?.replace(/^Event:\s*/i, '').trim() || '';

        // Date h3
        const h3 = container.querySelector('h3');
        const date = h3?.textContent?.replace(/^Date:\s*/i, '').trim() || '';

        // Takeaway div
        const takeaway = container.querySelector('.takeaway');
        const takeawayTitle = takeaway?.querySelector('h3, h2')?.textContent?.trim() || 'Key Takeaway Points';
        const takeawayItems = Array.from(takeaway?.querySelectorAll('li') || []).map(li => li.textContent.trim());

        setData({ speaker, title, eventName, date, takeawayTitle, takeawayItems });
      } catch (err) {
        console.error('[SummaryPanel] Error:', err);
        setError('Unable to load summary. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAndParse();
  }, [summaryDocId]);

  return (
    <div style={{
      background: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      marginTop: '20px',
      marginBottom: '16px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
      border: '1px solid #e2e8f0',
    }}>
      {/* Minimal header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 24px',
        background: `linear-gradient(135deg, ${accent}08, ${accent}03)`,
        borderBottom: '1px solid #f1f5f9',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '6px', height: '6px', borderRadius: '50%', background: accent,
          }} />
          <span style={{
            fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '1.5px', color: accent,
          }}>
            Lecture Summary
          </span>
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: '18px',
            cursor: 'pointer', color: '#94a3b8', padding: '4px 8px',
            borderRadius: '6px', lineHeight: 1,
          }}>✕</button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: '#94a3b8' }}>
          <div style={{
            width: '28px', height: '28px',
            border: '3px solid #e2e8f0', borderTop: `3px solid ${accent}`,
            borderRadius: '50%', animation: 'summarySpinner 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <span style={{ fontSize: '13px' }}>Loading summary...</span>
          <style>{`@keyframes summarySpinner { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {/* Content */}
      {!loading && !error && data && (
        <div style={{ padding: '24px' }}>

          {/* Hero — Title + Meta */}
          <div style={{ marginBottom: '24px' }}>
            {/* Event badge */}
            {data.eventName && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.5px', color: accent,
                background: `${accent}10`, padding: '4px 12px',
                borderRadius: '20px', marginBottom: '12px',
              }}>
                📚 {data.eventName}
              </div>
            )}

            {/* Title */}
            {data.title && (
              <h2 style={{
                fontSize: '22px', fontWeight: 700, color: '#0f172a',
                lineHeight: 1.3, margin: '0 0 16px 0',
                letterSpacing: '-0.3px',
              }}>
                {data.title}
              </h2>
            )}

            {/* Speaker + Date row */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              fontSize: '13px', color: '#64748b',
            }}>
              {data.speaker && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: `${accent}12`, color: accent,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 700,
                  }}>
                    {data.speaker.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                  </span>
                  <span style={{ fontWeight: 600, color: '#374151' }}>{data.speaker}</span>
                </span>
              )}
              {data.date && data.speaker && (
                <span style={{ color: '#d1d5db' }}>•</span>
              )}
              {data.date && <span>{data.date}</span>}
            </div>

            {/* Accent line */}
            <div style={{
              height: '2px', marginTop: '20px',
              background: `linear-gradient(to right, ${accent}, ${accent}30, transparent)`,
              borderRadius: '1px',
            }} />
          </div>

          {/* Key Takeaways */}
          {data.takeawayItems.length > 0 && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '16px',
              }}>
                <span style={{ fontSize: '16px' }}>✨</span>
                <span style={{
                  fontSize: '14px', fontWeight: 700, color: accent,
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  {data.takeawayTitle}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {data.takeawayItems.map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                    padding: '14px 16px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    borderLeft: `3px solid ${accent}40`,
                    transition: 'border-color 0.2s, background 0.2s',
                    lineHeight: 1.7,
                    fontSize: '14px',
                    color: '#374151',
                  }}>
                    <span style={{
                      minWidth: '24px', height: '24px', borderRadius: '50%',
                      background: `${accent}12`, color: accent,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 700, flexShrink: 0,
                      marginTop: '1px',
                    }}>
                      {idx + 1}
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SummaryPanel;
